import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * The self-view during a live roleplay: the preview the webcam-snapshot capture
 * (`useWebcamCapture`) draws its frames from, rendered as a small draggable tile.
 *
 * It used to be an identical hard-coded block in both AudioOrb and AudioOrbOpenAI, pinned
 * to the bottom-right corner — the same corner the fixed 피드백 보내기 button occupies, which
 * painted straight over the self-view. The fix is the default corner (it now clears the
 * button) plus the drag: anywhere the tile still lands badly, the trainee can move it.
 */

const MARGIN = 8;
const DEFAULT_RIGHT = 20;

/**
 * 200×150 is half the width of a 375px phone — the trainee is watching a customer, not
 * themselves, so the self-view shrinks rather than eating the screen. Same 4:3, so the
 * `object-fit: cover` framing does not change.
 */
const SIZE = { width: 200, height: 150 };
const NARROW_SIZE = { width: 120, height: 90 };
/** The width Layout already calls mobile. */
const NARROW_MAX_WIDTH = 768;
/** Clears the 피드백 보내기 button: it is fixed at bottom 30px and ~42px tall. */
const DEFAULT_BOTTOM = 84;

/**
 * Deliberately *below* the 피드백 보내기 button (1000), and above the session's own content
 * layer (10). Stacking order decides hit-testing, not just painting: with the tile on top,
 * a trainee who parks it over the button cannot click the button at all — and cannot report
 * that they cannot. A partly covered self-view is the cheaper failure, and it is one the
 * drag lets them undo.
 *
 * Anything a caller renders *over* the session — a consent modal, an error banner — still
 * has to sit above this; exported so those values read as one ladder instead of three
 * magic numbers.
 */
export const CAMERA_FEED_Z_INDEX = 900;

const STORAGE_KEY = "eos.cameraFeedPos";

/**
 * Keep `pos` inside `bounds`, leaving `margin` at every edge.
 *
 * The `Math.max(margin, …)` on the upper bounds matters when the container is smaller than
 * the tile (a short window): without it the allowed range inverts and the clamp pushes the
 * tile off the top-left instead of pinning it.
 */
export function clampToBounds(pos, size, bounds, margin = MARGIN) {
  const maxLeft = Math.max(margin, bounds.width - size.width - margin);
  const maxTop = Math.max(margin, bounds.height - size.height - margin);
  return {
    left: Math.min(Math.max(pos.left, margin), maxLeft),
    top: Math.min(Math.max(pos.top, margin), maxTop),
  };
}

/**
 * The tile's dimensions for the current viewport — a `resize` listener on `innerWidth`, the
 * way Layout and useIsNarrow already do it in this codebase.
 */
function useTileSize() {
  const [narrow, setNarrow] = useState(() => window.innerWidth <= NARROW_MAX_WIDTH);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth <= NARROW_MAX_WIDTH);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return narrow ? NARROW_SIZE : SIZE;
}

function readStoredPos() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Number.isFinite(parsed?.left) || !Number.isFinite(parsed?.top)) return null;
    return { left: parsed.left, top: parsed.top };
  } catch {
    // Private mode, disabled storage, or a hand-edited value. The default corner is fine.
    return null;
  }
}

const CameraFeed = ({ videoRef, isRecording }) => {
  const rootRef = useRef(null);
  const grabRef = useRef({ dx: 0, dy: 0 });
  const size = useTileSize();

  /**
   * `null` means "the default corner", still anchored in CSS with right/bottom — so before
   * anyone drags it, the tile follows a window resize for free. The first drag converts it
   * to explicit left/top coordinates.
   *
   * `pos` is only ever the *displayed* position, which the clamp may have pulled inward.
   * Where the user actually dropped the tile lives in `intentRef`, and that is what gets
   * persisted: clamping has to be non-destructive, because the container is not measurable
   * on every pass (it is 0×0 during the mount layout effect, and it is genuinely narrow on
   * a small window). Folding the clamp back into the stored value meant every reload
   * "restored" the tile to the top-left corner and overwrote the real position with it.
   */
  const [pos, setPos] = useState(null);
  const intentRef = useRef(null);
  /**
   * The drag guard is a ref, not the state below: a pointermove that arrives before React
   * has committed the pointerdown re-render would still read `dragging === false` from its
   * render closure and drop the move. The state exists only for the cursor and the save.
   */
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);

  const measure = () => {
    const el = rootRef.current;
    const parent = el && el.offsetParent;
    if (!parent) return null;
    const rect = el.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    // A degenerate container means "layout has not happened yet", not "no room" — clamping
    // against it would pin the tile to the corner.
    if (!parentRect.width || !parentRect.height) return null;
    return {
      el,
      rect,
      parentRect,
      size: { width: rect.width, height: rect.height },
      bounds: { width: parentRect.width, height: parentRect.height },
    };
  };

  /** Re-clamp the displayed position against the container as it is right now. */
  const applyBounds = () => {
    const intent = intentRef.current;
    if (!intent) return;
    const m = measure();
    setPos(m ? clampToBounds(intent, m.size, m.bounds) : intent);
  };

  useLayoutEffect(() => {
    const stored = readStoredPos();
    if (!stored) return;
    intentRef.current = stored;
    setPos(stored);
  }, []);

  const anchored = pos !== null;
  useEffect(() => {
    if (!anchored) return undefined;
    // Once, after the first paint (the mount layout effect is too early to measure), and
    // then whenever the window changes size — including growing back, which returns the
    // tile toward where the user actually put it.
    applyBounds();
    const onResize = () => applyBounds();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // Also on a size change: a tile parked at the right edge has different room once it
    // shrinks or grows, and the resize event alone fires before React has re-rendered it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchored, size.width, size.height]);

  // Written once the drag settles, not on every pointermove.
  useEffect(() => {
    if (dragging || !intentRef.current) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(intentRef.current));
    } catch {
      // Nothing to do — the position just will not survive a reload.
    }
  }, [dragging, pos]);

  const handlePointerDown = (e) => {
    if (e.button !== 0) return;
    const m = measure();
    if (!m) return;
    grabRef.current = { dx: e.clientX - m.rect.left, dy: e.clientY - m.rect.top };
    // Freeze the CSS-anchored default into explicit coordinates before the first move.
    const start = clampToBounds(
      { left: m.rect.left - m.parentRect.left, top: m.rect.top - m.parentRect.top },
      m.size,
      m.bounds
    );
    intentRef.current = start;
    setPos(start);
    draggingRef.current = true;
    setDragging(true);
    // Capture, so a fast drag that outruns the pointer keeps tracking outside the tile.
    m.el.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    const m = measure();
    if (!m) return;
    // The tile is dragged within the container, so where it lands *is* the user's intent.
    const next = clampToBounds(
      {
        left: e.clientX - m.parentRect.left - grabRef.current.dx,
        top: e.clientY - m.parentRect.top - grabRef.current.dy,
      },
      m.size,
      m.bounds
    );
    intentRef.current = next;
    setPos(next);
  };

  const endDrag = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    const el = rootRef.current;
    if (el?.hasPointerCapture?.(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
  };

  const resetPos = () => {
    intentRef.current = null;
    setPos(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignored: the in-memory reset above is what the user asked for.
    }
  };

  const placement = pos
    ? { left: `${pos.left}px`, top: `${pos.top}px` }
    : { right: `${DEFAULT_RIGHT}px`, bottom: `${DEFAULT_BOTTOM}px` };

  return (
    <div
      ref={rootRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={resetPos}
      title="드래그해서 위치를 옮길 수 있습니다 (더블클릭하면 원위치)"
      style={{
        position: "absolute",
        ...placement,
        width: `${size.width}px`,
        height: `${size.height}px`,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
        backgroundColor: "#000",
        zIndex: CAMERA_FEED_Z_INDEX,
        pointerEvents: "auto",
        border: isRecording
          ? "2px solid #10B981"
          : "2px solid rgba(255, 255, 255, 0.1)",
        transition: "border-color 0.3s ease",
        cursor: dragging ? "grabbing" : "grab",
        // Without this a touch drag scrolls the page instead of moving the tile.
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: "scaleX(-1)",
          // The drag is handled by the tile; the video must not swallow the pointer.
          pointerEvents: "none",
        }}
      />
      {/* Status Indicator */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          backgroundColor: isRecording ? "#10B981" : "#EF4444",
          boxShadow: "0 0 4px rgba(0,0,0,0.5)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          transition: "background-color 0.3s ease",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

export default CameraFeed;
